<template>
  <div class="seo-page">
    <LandingNavbar />
    <main class="seo-main">
      <header class="seo-hero">
        <p class="seo-kicker">KokeManga</p>
        <h1>{{ title }}</h1>
      </header>

      <!-- Privacy policy -->
      <article v-if="type === 'privacy'" class="legal-doc">
        <p class="intro">{{ $t('manga.privacy.intro') }}</p>
        <section v-for="(sec, i) in privacySections" :key="'p' + i" class="legal-section">
          <h2>{{ sec.title }}</h2>
          <p>{{ sec.content }}</p>
          <ul v-if="sec.items && sec.items.length">
            <li v-for="(item, j) in sec.items" :key="j">{{ item }}</li>
          </ul>
        </section>
      </article>

      <!-- Terms of service -->
      <article v-else class="legal-doc">
        <p class="update-date">{{ $t('manga.agreement.updateDate') }}</p>
        <p class="intro">{{ $t('manga.agreement.intro') }}</p>
        <section v-for="(sec, i) in agreementSections" :key="'a' + i" class="legal-section">
          <h2>{{ sec.title }}</h2>
          <p v-for="(p, k) in sec.paragraphs" :key="k">{{ p }}</p>
          <ul v-if="sec.items && sec.items.length">
            <li v-for="(item, j) in sec.items" :key="j">{{ item }}</li>
          </ul>
        </section>
      </article>
    </main>
    <footer class="seo-footer">
      <router-link :to="localPath('/')">{{ $t('nav.home') }}</router-link>
      <router-link :to="localPath('/faq')">{{ $t('seoPage.faq.h1') }}</router-link>
      <router-link :to="localPath('/guide')">{{ $t('seoPage.guide.h1') }}</router-link>
    </footer>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { localPath } from '@/i18n'
import LandingNavbar from '@/views/LandingNavbar.vue'

const route = useRoute()
const { t } = useI18n()

const type = computed(() => {
  const path = route.path || ''
  if (path.includes('agreement')) return 'agreement'
  return 'privacy'
})

const title = computed(() =>
  type.value === 'privacy' ? t('manga.footer.privacy') : t('manga.footer.agreement')
)

const privacySections = computed(() => {
  const p = {
    1: ['s1Title', 's1Content'],
    2: ['s2Title', 's2Content', 's2Item1', 's2Item2'],
    3: ['s3Title', 's3Content', 's3Item1', 's3Item2'],
    4: ['s4Title', 's4Content', 's4Item1', 's4Item2'],
    5: ['s5Title', 's5Content'],
    6: ['s6Title', 's6Content'],
    7: ['s7Title', 's7Content'],
    8: ['s8Title', 's8Content'],
  }
  return Object.values(p).map(([titleKey, contentKey, ...itemKeys]) => ({
    title: t(`manga.privacy.${titleKey}`),
    content: t(`manga.privacy.${contentKey}`),
    items: itemKeys.map((k) => t(`manga.privacy.${k}`)),
  }))
})

const agreementSections = computed(() => {
  const a = {
    1: ['s1Title', ['s1Content']],
    2: ['s2Title', ['s2Content'], ['s2Subtitle1', 's2Item1', 's2Item2', 's2Item3', 's2Item4', 's2Subtitle2', 's2Item5', 's2Item6']],
    3: ['s3Title', ['s3Content1', 's3Content2']],
    4: ['s4Title', ['s4Content1', 's4Content2']],
    5: ['s5Title', ['s5Content1', 's5Content2']],
    6: ['s6Title', ['s6Content'], ['s6Item1', 's6Item2', 's6Item3']],
    7: ['s7Title', ['s7Content']],
    8: ['s8Title', ['s8Content']],
    9: ['s9Title', [], ['s9Item1', 's9Item2']],
  }
  return Object.values(a).map(([titleKey, paraKeys, itemKeys]) => ({
    title: t(`manga.agreement.${titleKey}`),
    paragraphs: (paraKeys || []).map((k) => t(`manga.agreement.${k}`)),
    items: (itemKeys || []).map((k) => t(`manga.agreement.${k}`)),
  }))
})
</script>

<style scoped>
.seo-page {
  min-height: 100vh;
  background: #0b1020;
  color: #e8eefc;
}
.seo-main {
  max-width: 820px;
  margin: 0 auto;
  padding: 120px 20px 80px;
}
.seo-kicker {
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #7aa2ff;
  font-size: 13px;
  margin: 0 0 12px;
}
.seo-hero h1 {
  font-size: clamp(28px, 4vw, 36px);
  margin: 0 0 24px;
}
.legal-doc {
  line-height: 1.8;
  color: #d5deef;
}
.update-date {
  text-align: right;
  color: #8fa0c0;
  font-size: 14px;
}
.intro {
  margin: 0 0 24px;
  color: #c5d0e8;
}
.legal-section {
  margin: 28px 0;
}
.legal-section h2 {
  font-size: 20px;
  margin: 0 0 10px;
  color: #f0f4ff;
}
.legal-section p,
.legal-section li {
  margin: 0 0 10px;
  color: #c9d4ea;
}
.legal-section ul {
  padding-left: 1.25rem;
}
.seo-footer {
  max-width: 820px;
  margin: 0 auto;
  padding: 24px 20px 48px;
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
}
.seo-footer a {
  color: #8eb0ff;
  text-decoration: none;
}
</style>
